<template>
    <div class="py-12">
        <div class="rounded-lg bg-white shadow sm:py-10">
            <div class="mx-auto max-w-7xl px-6 lg:px-8">
                <div class="px-4 sm:px-0">
                    <h3 class="text-base font-semibold leading-7 text-gray-900">
                        {{ $t("vue.your_subscription") }}
                    </h3>
                </div>
                <div class="mt-6 border-t border-gray-100">
                    <dl class="divide-y divide-gray-100">
                        <div
                            class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0"
                        >
                            <dt
                                class="text-sm font-medium leading-6 text-gray-900"
                            >
                                {{ $t("vue.status") }}
                            </dt>
                            <dd
                                class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                            >
                                <div class="flex">
                                    <p
                                        v-if="
                                            globalProps.team.paddle_sub_active
                                        "
                                        class="mt-0.5 whitespace-nowrap rounded-md bg-green-50 px-1.5 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
                                    >
                                        {{ $t("vue.active") }}
                                    </p>
                                    <p
                                        v-else
                                        class="mt-0.5 whitespace-nowrap rounded-md bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20"
                                    >
                                        {{ $t("vue.inactive") }}
                                    </p>
                                </div>
                            </dd>
                        </div>
                        <div
                            class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0"
                        >
                            <dt
                                class="text-sm font-medium leading-6 text-gray-900"
                            >
                                {{ $t("vue.paid_domains") }}
                            </dt>
                            <dd
                                class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                            >
                                {{ subscription.items[0].quantity }}
                            </dd>
                        </div>
                        <div
                            class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0"
                        >
                            <dt
                                class="text-sm font-medium leading-6 text-gray-900"
                            >
                                {{ $t("vue.email_address") }}
                            </dt>
                            <dd
                                class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0"
                            >
                                {{ customer.email }}
                            </dd>
                        </div>
                        <div
                            class="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0"
                        >
                            <dt
                                class="text-sm font-medium leading-6 text-gray-900"
                            >
                                {{ $t("vue.invoices") }}
                            </dt>
                            <dd
                                class="mt-2 text-sm text-gray-900 sm:col-span-2 sm:mt-0"
                            >
                                <ul
                                    role="list"
                                    class="divide-y divide-gray-100 rounded-md border border-gray-200"
                                >
                                    <li
                                        v-for="transaction in transactions"
                                        :key="transaction.id"
                                        class="flex items-center justify-between py-4 pl-4 pr-5 text-sm leading-6"
                                    >
                                        <div
                                            class="flex w-0 flex-1 items-center"
                                        >
                                            <div
                                                class="ml-4 flex min-w-0 flex-1 gap-2"
                                            >
                                                <p
                                                    class="mt-0.5 whitespace-nowrap rounded-md bg-green-50 px-1.5 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
                                                >
                                                    {{ transaction.status }}
                                                </p>
                                                <span
                                                    class="truncate font-medium"
                                                    >{{
                                                        $t("vue.receipt_from")
                                                    }}
                                                    {{
                                                        toHumanDate(
                                                            transaction
                                                                .createdAt.date,
                                                        )
                                                    }}</span
                                                >
                                                <span
                                                    class="shrink-0 text-gray-400"
                                                    >${{
                                                        transaction.details
                                                            .totals.total / 100
                                                    }}</span
                                                >
                                            </div>
                                        </div>
                                        <div class="ml-4 shrink-0">
                                            <a
                                                :href="
                                                    route('checkout.invoice', {
                                                        transaction_id:
                                                            transaction.id,
                                                    })
                                                "
                                                class="font-medium text-indigo-600 hover:text-indigo-500"
                                                >{{ $t("vue.download") }}</a
                                            >
                                        </div>
                                    </li>
                                </ul>
                            </dd>
                        </div>
                    </dl>
                </div>

                <div class="sm:flex sm:items-center">
                    <div class="sm:flex-auto"></div>
                    <div class="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                        <div class="flex">
                            <a
                                :href="
                                    subscription.managementUrls
                                        .updatePaymentMethod
                                "
                                class="button mr-5 block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                >{{ $t("vue.update_payment_method") }}</a
                            >
                            <a
                                :href="subscription.managementUrls.cancel"
                                target="_blank"
                                class="button block rounded-md bg-red-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                                >{{ $t("vue.cancel_subscription") }}</a
                            >
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onMounted } from "vue";
import {
    initializePaddle,
    type Environments,
    type Paddle,
} from "@paddle/paddle-js";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import route from "ziggy-js";

const globalProps = computed(() => usePage<IRhizomPageProps>().props);

const props = defineProps({
    subscription: {
        type: Array,
        default: () => [],
    },
    customer: {
        type: Array,
        default: () => [],
    },
    transactions: {
        type: Array,
        default: () => [],
    },
    domains: {
        type: Number,
        default: () => 0,
    },
    paddleClientToken: {
        type: String,
        default: "",
    },
    paddleEnvironment: {
        type: String,
        default: "production",
    },
});

var myPaddleInstance: Paddle | null = null;

onMounted(() => {
    paddleInit();
});

const paddleInit = () => {
    console.log("paddleInit");
    console.log(globalProps.value.user.email);
    console.log(globalProps.value.team.uuid.toLowerCase());
    initializePaddle({
        environment: props.paddleEnvironment as Environments,
        token: props.paddleClientToken,
        pwCustomer: {
            id: globalProps.value.team.uuid.toLowerCase(),
            email: globalProps.value.user.email,
        },
        eventCallback: (event) => {
            handlePaddleResponse(event);
            /*
            console.log(event); // useful for debugging
            if (event.name == "checkout.loaded" || event.name == "checkout.items.updated") {
                // capture totals
                var todayTotal = event.data.totals.total;
                var recurringTotal = event.data.recurring_totals.total;
                var oneTimeTotal = todayTotal - recurringTotal;
                var currencyCode = event.data.currency_code;

                // display totals
                document.querySelector("#one-time-total").textContent = currencyCode + oneTimeTotal.toFixed(2);
                document.querySelector("#recurring-total").textContent = currencyCode + recurringTotal.toFixed(2);
                document.querySelector("#today-total").textContent = currencyCode + todayTotal.toFixed(2);
            };*/
        },
    }).then((paddleInstance: Paddle | undefined) => {
        if (paddleInstance) {
            myPaddleInstance = paddleInstance;
            console.log(paddleInstance);
        }
    });
};

const handlePaddleResponse = (event: any) => {
    console.log(event);
};

const toHumanDate = (createdAt: string) => {
    return new Date(createdAt).toLocaleDateString();
};
</script>
